/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  TranslationViewSegmentRow,
  Props as TranslationViewSegmentRowProps,
} from "./TranslationViewSegmentRow";
import { useSegmentation } from "./SegmentationHook";
import { PaginatorOptions, SegmentationItem } from "./types";
import { Button, Icon, Modal, ToggleCheckbox } from "../components/tailwind";

import { search } from "../products/copy-assistant/utils";
import { TRANSLATION_VIEW_SEGMENT_TABLE_CONTAINER_ID } from "./utils";
type Props = Omit<
  TranslationViewSegmentRowProps,
  "index" | "segmentationItem"
> & {
  sourceLanguage: string;
  targetLanguage: string;
  counts: ReturnType<typeof useSegmentation>["counts"];
  segmentationItems: SegmentationItem[];
  isSegmenting: boolean;
  isFetchingRegex: boolean;
  showCellPaginator?: boolean;
  paginatorOptions?: PaginatorOptions;
  onChangePaginator?: (opt: PaginatorOptions) => void;
};

const copySourceToTranslationShortcut = (): React.ReactNode => {
  const isMac =
    /Mac/i.test(navigator.platform) ||
    /Macintosh|Mac OS X/i.test(navigator.userAgent);

  if (!isMac) {
    return (
      <kbd>
        <kbd>Ctrl</kbd>+<kbd>Insert</kbd>
      </kbd>
    );
  }
  return (
    <kbd>
      <kbd>Cmd</kbd>+<kbd>Enter</kbd>
    </kbd>
  );
};

export const TranslationViewSegmentTable: React.FC<Props> = ({
  sourceLanguage,
  targetLanguage,
  segmentationItems,
  isFetchingRegex,
  isSegmenting,
  onChangePaginator,
  paginatorOptions,
  showCellPaginator,
  ...props
}) => {
  const paginatorModalSettingsRef = useRef<HTMLDialogElement>(null);
  const [autoFocusIndex, setAutoFocusIndex] = useState<number | null>(null);
  const [sourceQ, setSourceQ] = useState<string>("");
  const [targetQ, setTargetQ] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number | null>(null);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [paginatedItems, setPaginatedItems] = useState<
    SegmentationItem[] | null
  >(null);

  const findAutoFocusableIndex = (): number => {
    let index = segmentationItems.findIndex(
      (segment) => !segment.locked && !segment.translation_memory_id
    );
    if (index === -1) {
      index = segmentationItems.findIndex((segment) => !segment.locked);
    }
    return index || 0;
  };

  const filteredSegmentationItems = useMemo(() => {
    if (!sourceQ && !targetQ) return segmentationItems;
    return segmentationItems.filter((item) => {
      if (sourceQ && targetQ)
        return (
          search(item.source_text, sourceQ) &&
          search(item.target_text || "", targetQ)
        );
      if (sourceQ) {
        return search(item.source_text, sourceQ);
      }
      return search(item.target_text || "", targetQ);
    });
  }, [sourceQ, targetQ, segmentationItems]);

  useEffect(() => {
    setAutoFocusIndex(findAutoFocusableIndex());
  }, [segmentationItems]);

  const paginatorSegmentValue = useMemo(() => {
    if (!paginatorOptions) return "";
    if (paginatorOptions?.type != "segment") {
      return "";
    }
    return paginatorOptions.items;
  }, [Object.keys(paginatorOptions || {})]);

  useEffect(() => {
    if (!paginatorOptions) {
      setPaginatedItems(null);
      setTotalPages(null);
      setCurrentPage(null);
      return;
    }

    if (paginatorOptions.type === "segment") {
      const itemsPerPage = paginatorOptions.items;
      const newTotalPages = Math.ceil(
        filteredSegmentationItems.length / itemsPerPage
      );
      setTotalPages(newTotalPages);

      // Ensure current page is valid
      const validPage = Math.min(Math.max(1, currentPage || 1), newTotalPages);
      if (validPage !== currentPage) {
        setCurrentPage(validPage);
      }

      const startIndex = (validPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const newItems = filteredSegmentationItems.slice(startIndex, endIndex);
      setPaginatedItems(newItems);
    }

    // Cell-based pagination
    if (paginatorOptions.type === "cell") {
      const cellGroups = filteredSegmentationItems.reduce<
        Record<string, SegmentationItem[]>
      >((acc, item) => {
        if (!item.file_info) return acc;
        const key = `${item.file_info.row}-${item.file_info.column}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});

      const cells = Object.values(cellGroups);
      const newTotalPages = cells.length;
      setTotalPages(newTotalPages);

      // Ensure current page is valid
      const validPage = Math.min(Math.max(1, currentPage || 1), newTotalPages);
      if (validPage !== currentPage) {
        setCurrentPage(validPage);
      }

      const newItems = cells[validPage - 1] || [];
      setPaginatedItems(newItems);
    }

    if (paginatorOptions.type === "row") {
      const rowGroups = filteredSegmentationItems.reduce<
        Record<string, SegmentationItem[]>
      >((acc, item) => {
        if (!item.file_info) return acc;
        const key = `${item.file_info.row}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});

      const rows = Object.values(rowGroups);
      const newTotalPages = rows.length;
      setTotalPages(newTotalPages);

      // Ensure current page is valid
      const validPage = Math.min(Math.max(1, currentPage || 1), newTotalPages);
      if (validPage !== currentPage) {
        setCurrentPage(validPage);
      }

      const newItems = rows[validPage - 1] || [];
      setPaginatedItems(newItems);
    }
  }, [paginatorOptions, filteredSegmentationItems, currentPage]);

  const handleNextPage = (): void => {
    if (currentPage && totalPages && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = (): void => {
    if (currentPage && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const showPaginator = useMemo(() => {
    return !!currentPage && !!totalPages;
  }, [currentPage, paginatedItems, totalPages]);

  return (
    <>
      {showPaginator && (
        <div className="txu-paginator !tw-my-0 !tw-py-0">
          <ul>
            <li>
              <a
                data-balloon="Previous page"
                data-balloon-pos="down"
                className={`tw-cursor-pointer ${
                  !currentPage || currentPage <= 1 ? "disabled" : ""
                }`}
                onClick={handlePrevPage}
              >
                <i className="material-icons tw-leading-[1]">arrow_back</i>
              </a>
            </li>
            <li className="separator"></li>
            {currentPage &&
              totalPages &&
              ((): (JSX.Element | null)[] => {
                let pages: number[] = [];
                if (totalPages <= 7) {
                  pages = Array.from({ length: totalPages }, (_, i) => i + 1);
                } else {
                  pages.push(1);
                  for (
                    let i = Math.max(2, currentPage - 2);
                    i <= Math.min(totalPages - 1, currentPage + 2);
                    i++
                  ) {
                    pages.push(i);
                  }
                  pages.push(totalPages);
                }
                return pages.map((page) => (
                  <li
                    key={page}
                    className={page === currentPage ? "active" : ""}
                  >
                    <a
                      onClick={() => setCurrentPage(page)}
                      className="tw-cursor-pointer"
                    >
                      <span>{page}</span>
                    </a>
                  </li>
                ));
              })()}
            <li className="separator"></li>
            <li>
              <a
                data-balloon="Next page"
                data-balloon-pos="down"
                className={`tw-cursor-pointer ${
                  !currentPage || !totalPages || currentPage >= totalPages
                    ? "disabled"
                    : ""
                }`}
                onClick={handleNextPage}
              >
                <i className="material-icons tw-leading-[1]">arrow_forward</i>
              </a>
            </li>
          </ul>
        </div>
      )}
      <div
        className="tw-overflow-y-auto tw-flex-1 tw-min-h-0 tw-h-full"
        id={TRANSLATION_VIEW_SEGMENT_TABLE_CONTAINER_ID}
        data-testid="translation-view-segment-table-container"
      >
        <table
          data-testid="translation-segment-table"
          className="txu-table txu-very-compact txu-alternating-rows tw-h-full"
        >
          <thead className="tw-sticky tw-top-0 tw-z-10 tw-bg-white">
            <tr>
              <th className="!tw-pl-0">
                <Button
                  icon
                  size="sm"
                  compact
                  variant={paginatorOptions ? "primary" : "secondary-alt"}
                  data-balloon="Paginator settings"
                  data-balloon-pos="right"
                  type="button"
                  onClick={() => {
                    paginatorModalSettingsRef.current?.showModal();
                  }}
                >
                  <Icon name="layers" />
                </Button>
              </th>
              <th>
                <div
                  data-testid="translation-segment-source-header"
                  className="tw-flex tw-items-baseline tw-gap-4"
                >
                  <input
                    data-testid="translation-segment-source-search"
                    className="txu-input"
                    type="text"
                    placeholder="Search Source..."
                    value={sourceQ}
                    onChange={({ target }) => setSourceQ(target.value)}
                  />
                  <span
                    data-testid="translation-segment-source-language"
                    className="tw-text-xs tw-text-gray-500 tw-font-medium"
                  >
                    Language: <code>{sourceLanguage}</code>
                  </span>
                </div>
              </th>
              <th>
                <div
                  data-testid="translation-segment-target-header"
                  className="tw-flex tw-items-baseline tw-gap-4"
                >
                  <input
                    data-testid="translation-segment-target-search"
                    className="txu-input"
                    type="text"
                    placeholder="Search Translation..."
                    value={targetQ}
                    onChange={({ target }) => setTargetQ(target.value)}
                  />
                  <span
                    data-testid="translation-segment-target-language"
                    className="tw-text-xs tw-text-gray-500 tw-font-medium"
                  >
                    Language: <code>{targetLanguage}</code>
                  </span>
                  <div className="tw-flex tw-gap-1 tw-items-center tw-ml-auto">
                    <kbd
                      data-balloon="Navigate between segments"
                      data-balloon-pos="down-right"
                    >
                      Arrow Up/Down
                    </kbd>
                    {props.showLockIcons && (
                      <kbd
                        data-balloon="Lock segment"
                        data-balloon-pos="down-right"
                      >
                        <kbd>Ctrl</kbd>+<kbd>L</kbd>
                      </kbd>
                    )}
                    <kbd
                      data-balloon="Commit a segment to translation memory"
                      data-balloon-pos="down-right"
                    >
                      <kbd>Ctrl</kbd>+<kbd>Enter</kbd>
                    </kbd>
                    <kbd
                      data-balloon="Copy Source to Translation"
                      data-balloon-pos="down-right"
                    >
                      {copySourceToTranslationShortcut()}
                    </kbd>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          {isFetchingRegex || isSegmenting ? (
            <tbody>
              <tr>
                <td
                  colSpan={3}
                  data-testid="translation-segment-table-loading"
                  className="tw-text-center tw-animate-pulse"
                >
                  Loading...
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {!filteredSegmentationItems.length && (
                <tr>
                  <td></td>
                  <td className="tw-text-center tw-text-gray-500 tw-w-1/2">
                    No Sources found
                  </td>
                  <td className="tw-text-center tw-text-gray-500 tw-w-1/2">
                    No Translations found
                  </td>
                </tr>
              )}
              {(paginatedItems || filteredSegmentationItems)?.map(
                (segmentationItem, index) => {
                  if (!segmentationItem.source_text.trim()) {
                    return null;
                  }

                  return (
                    <TranslationViewSegmentRow
                      key={segmentationItem?.uuid || index}
                      index={
                        paginatedItems
                          ? filteredSegmentationItems.findIndex(
                              (item) => item === segmentationItem
                            )
                          : index
                      }
                      segmentationItem={segmentationItem}
                      autoFocus={
                        autoFocusIndex ===
                        (paginatedItems
                          ? filteredSegmentationItems.findIndex(
                              (item) => item === segmentationItem
                            )
                          : index)
                      }
                      {...props}
                    />
                  );
                }
              )}
            </tbody>
          )}
        </table>
      </div>
      <Modal dialogRef={paginatorModalSettingsRef}>
        <Modal.Header divide>
          <h4>Paginator settings</h4>
        </Modal.Header>
        <Modal.Body className="tw-flex tw-flex-col tw-gap-4">
          {showCellPaginator && (
            <>
              <span className="tw-text-gray-500 tw-text-sm">
                File specific Paginator
              </span>
              <div className="tw-flex tw-gap-4">
                <div className="tw-flex tw-gap-2">
                  <label>By Cells</label>
                  <ToggleCheckbox
                    checked={paginatorOptions?.type == "cell"}
                    setChecked={(checked) => {
                      if (checked) {
                        onChangePaginator({ type: "cell" });
                      } else {
                        onChangePaginator(null);
                      }
                    }}
                  />
                </div>
                <div className="tw-flex tw-gap-2">
                  <label>By Rows</label>
                  <ToggleCheckbox
                    checked={paginatorOptions?.type == "row"}
                    setChecked={(checked) => {
                      if (checked) {
                        onChangePaginator({ type: "row" });
                      } else {
                        onChangePaginator(null);
                      }
                    }}
                  />
                </div>
              </div>
              <div className="txu-divider" />
            </>
          )}
          <div>
            <label htmlFor="items-input">
              By Segments{" "}
              <span className="tw-text-gray-500 tw-text-sm">
                (max 500/per page)
              </span>
            </label>
            <input
              type="number"
              min={1}
              max={500}
              id="items-input"
              placeholder="10"
              value={paginatorSegmentValue}
              onChange={({ target }) => {
                const value = Number(target.value);
                if (!value) {
                  onChangePaginator(null);
                } else {
                  onChangePaginator({
                    type: "segment",
                    items: Math.min(value, 500),
                  });
                }
              }}
            />{" "}
            <span className="tw-text-gray-500">Segments per page</span>
          </div>
          <div className="txu-divider" />
          <Button
            content="Reset paginator"
            type="button"
            className="tw-mr-auto"
            onClick={() => onChangePaginator(null)}
          />
        </Modal.Body>
        <Modal.Actions>
          <Button
            content="Close"
            onClick={() => paginatorModalSettingsRef.current?.close()}
            type="button"
            size="sm"
          />
        </Modal.Actions>
      </Modal>
    </>
  );
};
